import {
  CancellationPolicyOption,
  DEFAULT_CHECK_IN_TIME,
  DEFAULT_CHECK_OUT_TIME,
  ICalendarDay,
  IListing,
  IRating,
  IReservation,
  ISite,
  RatingType,
  Reservation,
  User,
} from '@curbnturf/objects';
import { DateTime } from 'luxon';
import { forceInt } from './math';

export const generateReservationConfirmationCode = (reservation: IReservation): string => {
  return `r-${reservation.id}`;
};

export const getCancellationPolicyString = (cancellationPolicy: string): string => {
  switch (cancellationPolicy) {
    case 'easy': {
      return `Cancel up to one day prior to check-in to get a full refund. Any early check-outs will receive a full refund for all nights falling 24 hours after cancellation.`;
    }
    case 'moderate': {
      return `Cancel up to three days prior to check-in to get a full refund. Any early check-outs will receive a half refund for all nights falling 24 hours after cancellation.`;
    }
    case 'strict': {
      return `Cancel up to seven days prior to check-in to get a half refund. There are no refunds for early check-outs.`;
    }
    case 'exclusive': {
      return 'Any cancellation is non-refundable. There are no refunds for early check-outs.';
    }
    default:
      return '';
  }
};

export function containsCompleteRating(ratings?: IRating[]): boolean {
  return !!ratings?.find((rating) => rating.topic === RatingType.overall && rating.score.description);
}

export function lastDayToCancel(reservation: Reservation): DateTime | false {
  if (reservation.site?.listing) {
    let cancelDaysInAdvance;
    switch (reservation.site.listing.cancellationPolicy as string) {
      case 'exclusive': {
        return false;
      }
      case 'strict': {
        cancelDaysInAdvance = 7;
        break;
      }
      case 'moderate': {
        cancelDaysInAdvance = 3;
        break;
      }
      case 'easy':
      default: {
        cancelDaysInAdvance = 1;
      }
    }

    if (reservation.date && reservation.date.from) {
      let latestCancellableDate = DateTime.fromISO(reservation.date.from);
      latestCancellableDate = latestCancellableDate.minus({ day: cancelDaysInAdvance });
      return latestCancellableDate;
    }
  }
  return false;
}

export function isReservationCancellable(reservation: Reservation, currentUser: User): boolean {
  return Boolean(
    currentUser?.id &&
      reservation.cancellable(currentUser?.id)
  );
}

export function filterAvailableDays(availableDays: ICalendarDay[], site: ISite, listing: IListing): ICalendarDay[] {
  return availableDays.filter((day) => isDateValidForSite(day.date, site, listing));
}

export function isDateValidForSite(date: string, site: ISite, listing: IListing): boolean {
  if (!site || !listing) {
    return false;
  }

  // Filter out Blackout Days - Listing
  if (listing.blackoutDays && listing.blackoutDays.length > 0) {
    for (const blackoutDateRange of listing.blackoutDays) {
      if(blackoutDateRange.includes(':')) {
        const blackoutSplit = blackoutDateRange.split(':');
        const blackoutDateStart = DateTime.fromFormat(blackoutSplit[0], 'yyyy-MM-dd');
        const blackoutDateEnd = DateTime.fromFormat(blackoutSplit[1], 'yyyy-MM-dd');
        if (DateTime.fromISO(date) >= blackoutDateStart && DateTime.fromISO(date) <= blackoutDateEnd) {
          return false;
        }
      } else {
        const blackoutDate = DateTime.fromFormat(blackoutDateRange, 'yyyy-MM-dd');
        if (DateTime.fromISO(date) === blackoutDate) {
          return false;
        }
      }
    }
  }

  // Filter out Blackout Months - Listing
  if (listing.blackoutMonths && listing.blackoutMonths.length > 0) {
    for (const blackoutMonth of listing.blackoutMonths) {
      if (DateTime.fromISO(date).month === blackoutMonth) {
        return false;
      }
    }
  }

  // Filter out Blackout Months - Day of the Week
  if (listing.blackoutDayOfWeek && listing.blackoutDayOfWeek.length > 0) {
    for (const blackoutDayOfWeek of listing.blackoutDayOfWeek) {
      if (DateTime.fromISO(date).weekdayLong.toLowerCase() === blackoutDayOfWeek.toLowerCase()) {
        return false;
      }
    }
  }

  // Filter out Blackout Days - Site
  if (site.blackoutDays && site.blackoutDays.length > 0) {
    for (const blackoutDateRange of site.blackoutDays) {
      if (blackoutDateRange.includes(':')) {
        const blackoutSplit = blackoutDateRange.split(':');
        const blackoutDateStart = DateTime.fromFormat(blackoutSplit[0], 'yyyy-MM-dd');
        const blackoutDateEnd = DateTime.fromFormat(blackoutSplit[1], 'yyyy-MM-dd');
        if (DateTime.fromISO(date) >= blackoutDateStart && DateTime.fromISO(date) <= blackoutDateEnd) {
          return false;
        }
      } else {
        const blackoutDate = DateTime.fromFormat(blackoutDateRange, 'yyyy-MM-dd');
        if (DateTime.fromISO(date) === blackoutDate) {
          return false;
        }
      }
    }
  }

  // Filter out Blackout Months - Site
  if (site.blackoutMonths && site.blackoutMonths.length > 0) {
    for (const blackoutMonth of site.blackoutMonths) {
      if (DateTime.fromISO(date).month === blackoutMonth) {
        return false;
      }
    }
  }

  const reserveDaysInAdvance = forceInt(listing.reserveDaysInAdvance || 0);

  switch (reserveDaysInAdvance) {
    // if reserveDaysInAdvance = 0 then the can check in up until the check out time
    case 0: {
      return (
        DateTime.fromISO(`${date}T${listing.checkOut || DEFAULT_CHECK_OUT_TIME}`, {
          zone: listing.timezone || undefined,
        }).plus({
          days: 1,
        }) > DateTime.now()
      );
    }

    // by default if there is a reserveDaysInAdvance it is based of the checkIn
    default: {
      return (
        DateTime.fromISO(`${date}T${listing.checkIn || DEFAULT_CHECK_IN_TIME}`, { zone: listing.timezone || undefined }) >
        DateTime.now().plus({ days: reserveDaysInAdvance })
      );
    }
  }
}
